/**
 * F15 STEP 3.9 — A DERIVED (AUTO) WEEK ON THE PROGRAM BOARD, BY THE WRITER'S OWN PATH (DECISIONS 2026-10-31 "THE PROGRAM BOARD
 * IS THE UNIT"). An auto week holds no day rows: the board draws the last WRITTEN week before it, each day run through the
 * SAME pure chain `apply-next-week` runs for a client — `plan` → `review` → `automaticDecisions` → `bind` → `persist`
 * (`supabase/functions/_shared/nextWeekWriter.ts applyAutomatically`) — never a second planner.
 *
 * ⚠️ WHAT A TEMPLATE CAN HONESTLY SHOW. The board has no client, so there is NO LOG: the planner holds every ruled row
 * (`logged: null`) and the week reads as its source — except a DELOAD week, whose shape (half the sets rounded up, two reps
 * fewer, holds 20 % shorter, nothing to failure, same kilos, warm-ups kept) needs no log. What the client actually gets each
 * auto week is the same chain on THEIR log, written by the weekly adjustment; the board says "Auto" and shows the shape.
 */
import { automaticDecisions, bind, explain, persist, review, type SetWrite } from "../../../supabase/functions/_shared/coachNextWeek";
import { plan, type Plan } from "../../../supabase/functions/_shared/coachPlan";
import {
  EMPTY_CHECK_IN, LIBRARY_COLUMNS, SECTION_TREE_SELECT, engineOf, workoutRows, type Db, type LibraryRowDb, type SectionRowDb,
  type WorkoutRows,
} from "../../../supabase/functions/_shared/nextWeekWriter";
import type { WorkoutRulesEngine } from "../../../supabase/functions/_shared/workoutRules";
import type { AssignedWorkoutTree } from "./assignedWorkoutTree";
import type { BoardDay } from "./programBoard";

/** What one day of an auto week is: the source template's sets, or the chain's writes over them. */
export type DerivedDay = {
  workoutId: string;
  isDeload: boolean;
  /** `unchanged` — the chain had nothing to write (no log on a template); `changed` — its writes; `refused` — said, drawn as is. */
  kind: "unchanged" | "changed" | "refused";
  writes: SetWrite[];
  deletions: string[];
  /** The review's first lines (the deload sentence first), for the card's title. */
  said: string[];
  /** Why the chain wrote nothing on a `refused` day — the writer's own sentence. */
  reason?: string;
};

/** The pure half: one template's rows + engine → the writer's answer. Timed by the board (`deriveWeeks`). */
export function deriveDay(workoutId: string, w: WorkoutRows, engine: WorkoutRulesEngine, isDeload: boolean): DerivedDay {
  let n = 0;
  const built: Plan = plan({
    day: { assignmentId: `board-${workoutId}`, date: "2000-01-03", rootWorkoutId: workoutId, rows: w.rows },
    logged: null, history: [], weekNumber: 2, isDeload, progressSwitchedOff: !engine.progress,
    checkIn: EMPTY_CHECK_IN, musclesByExerciseId: {}, ladderTargets: {}, mint: () => `introduced-${String(++n)}`,
  });
  const r = review(built);
  const said = built.isDeloadWeek ? ["Deload week"] : [];
  /* A unit the PLANNER refused (a deload that would re-split a superset) writes nothing and is said — first refusal wins. */
  const refusedBy = built.plans.find((u) => u.outcome.kind === "refused")?.outcome;
  const planRefusal = refusedBy !== undefined && "reason" in refusedBy ? refusedBy.reason : undefined;
  if (r.suggestions.length === 0) {
    return planRefusal === undefined ? { workoutId, isDeload, kind: "unchanged", writes: [], deletions: [], said }
      : { workoutId, isDeload, kind: "refused", writes: [], deletions: [], said, reason: planRefusal };
  }
  const bound = bind(r, automaticDecisions(r), w.rows);
  if (bound.kind !== "bound") return { workoutId, isDeload, kind: "refused", writes: [], deletions: [], said, reason: explain(bound) };
  const p = persist(bound.writes, w.setsByRow);
  if (p.kind !== "ready") return { workoutId, isDeload, kind: "refused", writes: [], deletions: [], said, reason: p.reason };
  return { workoutId, isDeload, kind: "changed", writes: p.persistence.writes, deletions: p.persistence.deletions, said };
}

/** The source of every auto week: the nearest WRITTEN week before it (week 1 is always written — the table's CHECK). */
export function sourceWeekOf(week: number, derived: ReadonlySet<number>): number {
  let w = week;
  while (w > 1 && derived.has(w)) w -= 1;
  return w;
}

/** The cards an auto week draws: its source week's, at the auto week's index (read-only ids, prefixed so no drag finds them). */
export function derivedCards(days: readonly BoardDay[], week: number, derived: ReadonlySet<number>): BoardDay[] {
  if (!derived.has(week)) return [];
  const from = sourceWeekOf(week, derived);
  return days.filter((d) => d.weekIndex === from).map((d) => ({ ...d, id: `auto-${String(week)}-${d.id}`, weekIndex: week }));
}

/** The tree an auto deload day draws: the template's, its sets replaced by the chain's writes (deletions dropped). */
export function derivedTree(tree: AssignedWorkoutTree, d: DerivedDay): AssignedWorkoutTree {
  if (d.kind !== "changed") return tree;
  const byRow = new Map<string, SetWrite[]>();
  for (const w of d.writes) byRow.set(w.rowId.toLowerCase(), [...(byRow.get(w.rowId.toLowerCase()) ?? []), w]);
  const gone = new Set(d.deletions.map((x) => x.toLowerCase()));
  return {
    ...tree,
    id: `auto-${tree.id}`,
    coach_workout_sections: (tree.coach_workout_sections ?? []).map((s) => ({
      ...s,
      coach_workout_exercises: (s.coach_workout_exercises ?? []).map((e) => {
        const writes = byRow.get(e.id.toLowerCase());
        if (writes === undefined) return e;
        const kept = (e.coach_workout_sets ?? []).filter((x) => !gone.has(x.id.toLowerCase()));
        const merged = kept.map((x) => {
          const w = writes.find((y) => y.id !== null && y.id.toLowerCase() === x.id.toLowerCase());
          return w === undefined ? x : { ...x, position: w.set.position, reps: w.set.reps, weight_kg: w.set.weightKg,
                                         time_seconds: w.set.timeSeconds, rest_seconds: w.set.restSeconds, set_type: w.set.setType };
        });
        return { ...e, coach_workout_sets: merged.sort((a, b) => (a.position ?? 0) - (b.position ?? 0)) };
      }),
    })),
  };
}

type Q<T> = PromiseLike<{ data: T[] | null; error: { message: string } | null }>;
async function all<T>(q: Q<T>): Promise<T[]> {
  const { data, error } = await q;
  if (error !== null) throw new Error(error.message);
  return data ?? [];
}

/**
 * Read every template the auto weeks draw in TWO round trips — switches + rules + section trees in parallel, then the library
 * rows they name — and assemble each one with the WRITER's own pure halves (`engineOf`, `workoutRows`; `readEngine` and
 * `rowsOfWorkout` are those plus a per-workout read). ⚠️ MEASURED 2026-10-31: the per-workout readers cost 3–4 sequential
 * round trips per template, 5.6 s median over William's 11 templates on the live network; this shape is two.
 */
export async function deriveWeeks(db: Db, wanted: readonly { workoutId: string; isDeload: boolean }[],
                                  /** F15 4.2 — the program's switch (`coach_programs.progress`), over each workout's own. */
                                  programProgress?: boolean):
  Promise<{ days: Map<string, DerivedDay>; readMs: number; deriveMs: number }> {
  const t0 = performance.now();
  const ids = [...new Set(wanted.map((x) => x.workoutId))];
  const [switches, rules, sections] = await Promise.all([
    all<{ workout_id: string; progress: boolean; deload_every: number | null }>(
      db.from("coach_workout_engine").select("workout_id, progress, deload_every").in("workout_id", ids)),
    all<{ workout_id: string; exercise_id: string; progression_config: unknown }>(
      db.from("coach_workout_rules").select("workout_id, exercise_id, progression_config").in("workout_id", ids)),
    all<SectionRowDb & { workout_id: string }>(
      db.from("coach_workout_sections").select(`workout_id, ${SECTION_TREE_SELECT}`).in("workout_id", ids)),
  ]);
  const exerciseIds = [...new Set(sections.flatMap((s) => (s.coach_workout_exercises ?? []).map((e) => e.exercise_id)))];
  const library = exerciseIds.length === 0 ? []
    : await all<LibraryRowDb>(db.from("coach_exercises").select(LIBRARY_COLUMNS).in("id", exerciseIds));
  const read = new Map<string, { rows: WorkoutRows; engine: WorkoutRulesEngine }>();
  for (const id of ids) {
    const own = engineOf(switches.filter((s) => s.workout_id === id), rules.filter((r) => r.workout_id === id));
    const engine = programProgress === undefined ? own : { ...own, progress: programProgress };
    read.set(id, { rows: workoutRows(sections.filter((s) => s.workout_id === id), library, engine), engine });
  }
  const t1 = performance.now();
  const days = new Map<string, DerivedDay>();
  for (const x of wanted) {
    const k = derivedKey(x.workoutId, x.isDeload);
    const r = read.get(x.workoutId);
    if (r !== undefined && !days.has(k)) days.set(k, deriveDay(x.workoutId, r.rows, r.engine, x.isDeload));
  }
  return { days, readMs: t1 - t0, deriveMs: performance.now() - t1 };
}

export const derivedKey = (workoutId: string, isDeload: boolean): string => `${workoutId}:${isDeload ? "deload" : "load"}`;
